import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
  writeBatch,
  type QueryConstraint,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestore } from '@core/services/firebase/firebase-service'
import { ActivityCommentConverter } from '@core/services/activity-comments/converters/activity-comment-converter'
import { ActivityCommentsErrorConverter } from '@core/services/activity-comments/converters/activity-comments-error-converter'
import { CommentReplyConverter } from '@core/services/activity-comments/converters/comment-reply-converter'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'
import type {
  ActivityComment,
  CommentReply,
} from '@domain/models/activity-comments'

const ACTIVITY_COMMENTS_COLLECTION = 'activity_comments'
const ACTIVITIES_EXPLORE_COLLECTION = 'activities_explore'
const WEBINARS_COLLECTION = 'webinars'
const PODCAST_EPISODES_COLLECTION = 'podcast_episodes'
const INSTAGRAM_REELS_COLLECTION = 'instagram_reels'
const WEBINAR_SUGGESTIONS_COLLECTION = 'webinar_suggestions'
const PD_SYMPTOMS_COLLECTION = 'pd_symptoms'
const WHAT_WORKS_SUBCOLLECTION = 'what_works'
const COMMENT_REPLIES_SUBCOLLECTION = 'comment_replies'
const USERS_COLLECTION = 'users'

export interface ObserveActivityExploreCommentsOptions {
  descending?: boolean
  limitCount?: number
}

export interface ObserveCommentRepliesOptions {
  descending?: boolean
  limitCount?: number
}

function getActivityCommentsCollection() {
  if (!firestore) {
    throw new Error('Firestore is not initialized')
  }
  return collection(firestore, ACTIVITY_COMMENTS_COLLECTION)
}

function getActivityExploreDoc(activityExploreId: string) {
  if (!firestore) {
    throw new Error('Firestore is not initialized')
  }
  return doc(firestore, ACTIVITIES_EXPLORE_COLLECTION, activityExploreId)
}

function getWebinarDoc(webinarId: string) {
  if (!firestore) {
    throw new Error('Firestore is not initialized')
  }
  return doc(firestore, WEBINARS_COLLECTION, webinarId)
}

function getPodcastEpisodeDoc(episodeId: string) {
  if (!firestore) {
    throw new Error('Firestore is not initialized')
  }
  return doc(firestore, PODCAST_EPISODES_COLLECTION, episodeId)
}

function getInstagramReelDoc(reelId: string) {
  if (!firestore) {
    throw new Error('Firestore is not initialized')
  }
  return doc(firestore, INSTAGRAM_REELS_COLLECTION, reelId)
}

function getWebinarSuggestionDoc(suggestionId: string) {
  if (!firestore) {
    throw new Error('Firestore is not initialized')
  }
  return doc(firestore, WEBINAR_SUGGESTIONS_COLLECTION, suggestionId)
}

function getWhatWorkDoc(symptomId: string, whatWorksId: string) {
  if (!firestore) {
    throw new Error('Firestore is not initialized')
  }

  return doc(
    firestore,
    PD_SYMPTOMS_COLLECTION,
    symptomId,
    WHAT_WORKS_SUBCOLLECTION,
    whatWorksId,
  )
}

function getUserDoc(userId: string) {
  if (!firestore) {
    throw new Error('Firestore is not initialized')
  }
  return doc(firestore, USERS_COLLECTION, userId)
}

function getActivityCommentDoc(commentId: string) {
  return doc(getActivityCommentsCollection(), commentId)
}

function getCommentRepliesCollection(commentId: string) {
  return collection(getActivityCommentDoc(commentId), COMMENT_REPLIES_SUBCOLLECTION)
}

function buildActivityExploreCommentsQuery(
  activityExploreId: string,
  options?: ObserveActivityExploreCommentsOptions,
) {
  const constraints: QueryConstraint[] = [
    where('activity_ref', '==', getActivityExploreDoc(activityExploreId)),
    orderBy('posted_on', options?.descending === false ? 'asc' : 'desc'),
  ]

  if (options?.limitCount && options.limitCount > 0) {
    constraints.push(limit(options.limitCount))
  }

  return query(getActivityCommentsCollection(), ...constraints)
}

function buildWebinarCommentsQuery(
  webinarId: string,
  options?: ObserveActivityExploreCommentsOptions,
) {
  const constraints: QueryConstraint[] = [
    where('activity_ref', '==', getWebinarDoc(webinarId)),
    orderBy('posted_on', options?.descending === false ? 'asc' : 'desc'),
  ]

  if (options?.limitCount && options.limitCount > 0) {
    constraints.push(limit(options.limitCount))
  }

  return query(getActivityCommentsCollection(), ...constraints)
}

function buildPodcastCommentsQuery(
  podcastEpisodeId: string,
  options?: ObserveActivityExploreCommentsOptions,
) {
  const constraints: QueryConstraint[] = [
    where('activity_ref', '==', getPodcastEpisodeDoc(podcastEpisodeId)),
    orderBy('posted_on', options?.descending === false ? 'asc' : 'desc'),
  ]

  if (options?.limitCount && options.limitCount > 0) {
    constraints.push(limit(options.limitCount))
  }

  return query(getActivityCommentsCollection(), ...constraints)
}

function buildInstagramReelCommentsQuery(
  reelId: string,
  options?: ObserveActivityExploreCommentsOptions,
) {
  const constraints: QueryConstraint[] = [
    where('activity_ref', '==', getInstagramReelDoc(reelId)),
    orderBy('posted_on', options?.descending === false ? 'asc' : 'desc'),
  ]

  if (options?.limitCount && options.limitCount > 0) {
    constraints.push(limit(options.limitCount))
  }

  return query(getActivityCommentsCollection(), ...constraints)
}

function buildWebinarSuggestionCommentsQuery(
  suggestionId: string,
  options?: ObserveActivityExploreCommentsOptions,
) {
  const constraints: QueryConstraint[] = [
    where('activity_ref', '==', getWebinarSuggestionDoc(suggestionId)),
    orderBy('posted_on', options?.descending === false ? 'asc' : 'desc'),
  ]

  if (options?.limitCount && options.limitCount > 0) {
    constraints.push(limit(options.limitCount))
  }

  return query(getActivityCommentsCollection(), ...constraints)
}

function buildActivityExploreCommentsCountQuery(activityExploreId: string) {
  return query(
    getActivityCommentsCollection(),
    where('activity_ref', '==', getActivityExploreDoc(activityExploreId)),
  )
}

function buildWebinarCommentsCountQuery(webinarId: string) {
  return query(
    getActivityCommentsCollection(),
    where('activity_ref', '==', getWebinarDoc(webinarId)),
  )
}

function buildPodcastCommentsCountQuery(podcastEpisodeId: string) {
  return query(
    getActivityCommentsCollection(),
    where('activity_ref', '==', getPodcastEpisodeDoc(podcastEpisodeId)),
  )
}

function buildInstagramReelCommentsCountQuery(reelId: string) {
  return query(
    getActivityCommentsCollection(),
    where('activity_ref', '==', getInstagramReelDoc(reelId)),
  )
}

function buildWebinarSuggestionCommentsCountQuery(suggestionId: string) {
  return query(
    getActivityCommentsCollection(),
    where('activity_ref', '==', getWebinarSuggestionDoc(suggestionId)),
  )
}

function buildSymptomCommentsQuery(
  symptomId: string,
  whatWorksId: string,
  options?: ObserveActivityExploreCommentsOptions,
) {
  const constraints: QueryConstraint[] = [
    where('solution_ref', '==', getWhatWorkDoc(symptomId, whatWorksId)),
    orderBy('posted_on', options?.descending === false ? 'asc' : 'desc'),
  ]

  if (options?.limitCount && options.limitCount > 0) {
    constraints.push(limit(options.limitCount))
  }

  return query(getActivityCommentsCollection(), ...constraints)
}

function buildSymptomCommentsCountQuery(symptomId: string, whatWorksId: string) {
  return query(
    getActivityCommentsCollection(),
    where('solution_ref', '==', getWhatWorkDoc(symptomId, whatWorksId)),
  )
}

function buildCommentRepliesQuery(
  commentId: string,
  options?: ObserveCommentRepliesOptions,
) {
  const constraints: QueryConstraint[] = [
    orderBy('posted_on', options?.descending === false ? 'asc' : 'desc'),
  ]

  if (options?.limitCount && options.limitCount > 0) {
    constraints.push(limit(options.limitCount))
  }

  return query(getCommentRepliesCollection(commentId), ...constraints)
}

export class ActivityCommentsService {
  private static instance: ActivityCommentsService

  private constructor() {}

  public static getInstance(): ActivityCommentsService {
    if (!ActivityCommentsService.instance) {
      ActivityCommentsService.instance = new ActivityCommentsService()
    }
    return ActivityCommentsService.instance
  }

  public async createActivityComment(
    input: Partial<ActivityComment>,
  ): Promise<string> {
    try {
      const payload = ActivityCommentConverter.toFirestore({
        ...input,
        postedOn: input.postedOn ?? new Date(),
        likes: input.likes ?? 0,
        likedBy: input.likedBy ?? [],
      })
      const createdDoc = await addDoc(getActivityCommentsCollection(), payload)
      return createdDoc.id
    } catch (error) {
      throw ActivityCommentsErrorConverter.domainModel(error)
    }
  }

  public async fetchActivityComment(commentId: string): Promise<ActivityComment> {
    try {
      const snapshot = await getDoc(getActivityCommentDoc(commentId))
      if (!snapshot.exists()) {
        throw new CoreError({
          cause: CoreErrorCause.DataNotFound,
          context: `Activity comment "${commentId}" was not found.`,
        })
      }

      return ActivityCommentConverter.fromFirestore(
        snapshot.id,
        snapshot.data() as Record<string, unknown>,
      )
    } catch (error) {
      throw ActivityCommentsErrorConverter.domainModel(error)
    }
  }

  public observeActivityComment(
    commentId: string,
    callback: (comment: ActivityComment | null) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    return onSnapshot(
      getActivityCommentDoc(commentId),
      (snapshot) => {
        if (!snapshot.exists()) {
          callback(null)
          return
        }
        callback(
          ActivityCommentConverter.fromFirestore(
            snapshot.id,
            snapshot.data() as Record<string, unknown>,
          ),
        )
      },
      (error) => {
        onError?.(ActivityCommentsErrorConverter.domainModel(error))
      },
    )
  }

  public async updateActivityComment(
    commentId: string,
    comment: string,
  ): Promise<void> {
    try {
      await updateDoc(getActivityCommentDoc(commentId), {
        comment,
        posted_on: new Date(),
      })
    } catch (error) {
      throw ActivityCommentsErrorConverter.domainModel(error)
    }
  }

  public async deleteActivityComment(commentId: string): Promise<void> {
    try {
      if (!firestore) {
        throw new Error('Firestore is not initialized')
      }

      const commentDoc = getActivityCommentDoc(commentId)
      const repliesSnapshot = await getDocs(getCommentRepliesCollection(commentId))
      const batch = writeBatch(firestore)

      repliesSnapshot.docs.forEach((replyDoc) => {
        batch.delete(replyDoc.ref)
      })
      batch.delete(commentDoc)

      await batch.commit()
    } catch (error) {
      throw ActivityCommentsErrorConverter.domainModel(error)
    }
  }

  public async likeComment(commentId: string, userId: string): Promise<void> {
    try {
      await updateDoc(getActivityCommentDoc(commentId), {
        likes: increment(1),
        liked_by: arrayUnion(getUserDoc(userId)),
      })
    } catch (error) {
      throw ActivityCommentsErrorConverter.domainModel(error)
    }
  }

  public async unlikeComment(commentId: string, userId: string): Promise<void> {
    try {
      await updateDoc(getActivityCommentDoc(commentId), {
        likes: increment(-1),
        liked_by: arrayRemove(getUserDoc(userId)),
      })
    } catch (error) {
      throw ActivityCommentsErrorConverter.domainModel(error)
    }
  }

  public async createCommentReply(
    commentId: string,
    input: Partial<CommentReply>,
  ): Promise<string> {
    try {
      const payload = CommentReplyConverter.toFirestore({
        ...input,
        postedOn: input.postedOn ?? new Date(),
        likes: input.likes ?? 0,
        likedBy: input.likedBy ?? [],
      })
      const createdDoc = await addDoc(getCommentRepliesCollection(commentId), payload)
      return createdDoc.id
    } catch (error) {
      throw ActivityCommentsErrorConverter.domainModel(error)
    }
  }

  public async fetchActivityCommentReply(
    commentId: string,
    replyId: string,
  ): Promise<CommentReply> {
    try {
      const snapshot = await getDoc(doc(getCommentRepliesCollection(commentId), replyId))
      if (!snapshot.exists()) {
        throw new CoreError({
          cause: CoreErrorCause.DataNotFound,
          context: `Comment reply "${replyId}" was not found.`,
        })
      }

      return CommentReplyConverter.fromFirestore(
        snapshot.id,
        snapshot.data() as Record<string, unknown>,
      )
    } catch (error) {
      throw ActivityCommentsErrorConverter.domainModel(error)
    }
  }

  public observeCommentReplies(
    commentId: string,
    callback: (replies: CommentReply[]) => void,
    onError?: (error: Error) => void,
    options?: ObserveCommentRepliesOptions,
  ): Unsubscribe {
    return onSnapshot(
      buildCommentRepliesQuery(commentId, options),
      (snapshot) => {
        callback(
          snapshot.docs.map((item) =>
            CommentReplyConverter.fromFirestore(
              item.id,
              item.data() as Record<string, unknown>,
            ),
          ),
        )
      },
      (error) => {
        onError?.(ActivityCommentsErrorConverter.domainModel(error))
      },
    )
  }

  public async updateActivityCommentReply(
    commentId: string,
    replyId: string,
    commentReply: string,
  ): Promise<void> {
    try {
      await updateDoc(doc(getCommentRepliesCollection(commentId), replyId), {
        comment_reply: commentReply,
        posted_on: new Date(),
      })
    } catch (error) {
      throw ActivityCommentsErrorConverter.domainModel(error)
    }
  }

  public async deleteActivityCommentReply(
    commentId: string,
    replyId: string,
  ): Promise<void> {
    try {
      await deleteDoc(doc(getCommentRepliesCollection(commentId), replyId))
    } catch (error) {
      throw ActivityCommentsErrorConverter.domainModel(error)
    }
  }

  public async likeCommentReply(
    commentId: string,
    replyId: string,
    userId: string,
  ): Promise<void> {
    try {
      await updateDoc(doc(getCommentRepliesCollection(commentId), replyId), {
        likes: increment(1),
        liked_by: arrayUnion(getUserDoc(userId)),
      })
    } catch (error) {
      throw ActivityCommentsErrorConverter.domainModel(error)
    }
  }

  public async unlikeCommentReply(
    commentId: string,
    replyId: string,
    userId: string,
  ): Promise<void> {
    try {
      await updateDoc(doc(getCommentRepliesCollection(commentId), replyId), {
        likes: increment(-1),
        liked_by: arrayRemove(getUserDoc(userId)),
      })
    } catch (error) {
      throw ActivityCommentsErrorConverter.domainModel(error)
    }
  }

  public async fetchActivityExploreComments(
    activityExploreId: string,
    options?: ObserveActivityExploreCommentsOptions,
  ): Promise<ActivityComment[]> {
    try {
      const snapshot = await getDocs(
        buildActivityExploreCommentsQuery(activityExploreId, options),
      )
      return snapshot.docs.map((item) =>
        ActivityCommentConverter.fromFirestore(
          item.id,
          item.data() as Record<string, unknown>,
        ),
      )
    } catch (error) {
      throw ActivityCommentsErrorConverter.domainModel(error)
    }
  }

  public async fetchWebinarComments(
    webinarId: string,
    options?: ObserveActivityExploreCommentsOptions,
  ): Promise<ActivityComment[]> {
    try {
      const snapshot = await getDocs(
        buildWebinarCommentsQuery(webinarId, options),
      )
      return snapshot.docs.map((item) =>
        ActivityCommentConverter.fromFirestore(
          item.id,
          item.data() as Record<string, unknown>,
        ),
      )
    } catch (error) {
      throw ActivityCommentsErrorConverter.domainModel(error)
    }
  }

  public async fetchPodcastComments(
    podcastEpisodeId: string,
    options?: ObserveActivityExploreCommentsOptions,
  ): Promise<ActivityComment[]> {
    try {
      const snapshot = await getDocs(
        buildPodcastCommentsQuery(podcastEpisodeId, options),
      )
      return snapshot.docs.map((item) =>
        ActivityCommentConverter.fromFirestore(
          item.id,
          item.data() as Record<string, unknown>,
        ),
      )
    } catch (error) {
      throw ActivityCommentsErrorConverter.domainModel(error)
    }
  }

  public async fetchInstagramReelComments(
    reelId: string,
    options?: ObserveActivityExploreCommentsOptions,
  ): Promise<ActivityComment[]> {
    try {
      const snapshot = await getDocs(
        buildInstagramReelCommentsQuery(reelId, options),
      )
      return snapshot.docs.map((item) =>
        ActivityCommentConverter.fromFirestore(
          item.id,
          item.data() as Record<string, unknown>,
        ),
      )
    } catch (error) {
      throw ActivityCommentsErrorConverter.domainModel(error)
    }
  }

  public async fetchWebinarSuggestionComments(
    suggestionId: string,
    options?: ObserveActivityExploreCommentsOptions,
  ): Promise<ActivityComment[]> {
    try {
      const snapshot = await getDocs(
        buildWebinarSuggestionCommentsQuery(suggestionId, options),
      )
      return snapshot.docs.map((item) =>
        ActivityCommentConverter.fromFirestore(
          item.id,
          item.data() as Record<string, unknown>,
        ),
      )
    } catch (error) {
      throw ActivityCommentsErrorConverter.domainModel(error)
    }
  }

  public async fetchSymptomComments(
    symptomId: string,
    whatWorksId: string,
    options?: ObserveActivityExploreCommentsOptions,
  ): Promise<ActivityComment[]> {
    try {
      const snapshot = await getDocs(
        buildSymptomCommentsQuery(symptomId, whatWorksId, options),
      )
      return snapshot.docs.map((item) =>
        ActivityCommentConverter.fromFirestore(
          item.id,
          item.data() as Record<string, unknown>,
        ),
      )
    } catch (error) {
      throw ActivityCommentsErrorConverter.domainModel(error)
    }
  }

  public observeActivityExploreComments(
    activityExploreId: string,
    callback: (comments: ActivityComment[]) => void,
    onError?: (error: Error) => void,
    options?: ObserveActivityExploreCommentsOptions,
  ): Unsubscribe {
    return onSnapshot(
      buildActivityExploreCommentsQuery(activityExploreId, options),
      (snapshot) => {
        callback(
          snapshot.docs.map((item) =>
            ActivityCommentConverter.fromFirestore(
              item.id,
              item.data() as Record<string, unknown>,
            ),
          ),
        )
      },
      (error) => {
        onError?.(ActivityCommentsErrorConverter.domainModel(error))
      },
    )
  }

  public observeWebinarComments(
    webinarId: string,
    callback: (comments: ActivityComment[]) => void,
    onError?: (error: Error) => void,
    options?: ObserveActivityExploreCommentsOptions,
  ): Unsubscribe {
    return onSnapshot(
      buildWebinarCommentsQuery(webinarId, options),
      (snapshot) => {
        callback(
          snapshot.docs.map((item) =>
            ActivityCommentConverter.fromFirestore(
              item.id,
              item.data() as Record<string, unknown>,
            ),
          ),
        )
      },
      (error) => {
        onError?.(ActivityCommentsErrorConverter.domainModel(error))
      },
    )
  }

  public observePodcastComments(
    podcastEpisodeId: string,
    callback: (comments: ActivityComment[]) => void,
    onError?: (error: Error) => void,
    options?: ObserveActivityExploreCommentsOptions,
  ): Unsubscribe {
    return onSnapshot(
      buildPodcastCommentsQuery(podcastEpisodeId, options),
      (snapshot) => {
        callback(
          snapshot.docs.map((item) =>
            ActivityCommentConverter.fromFirestore(
              item.id,
              item.data() as Record<string, unknown>,
            ),
          ),
        )
      },
      (error) => {
        onError?.(ActivityCommentsErrorConverter.domainModel(error))
      },
    )
  }

  public observeInstagramReelComments(
    reelId: string,
    callback: (comments: ActivityComment[]) => void,
    onError?: (error: Error) => void,
    options?: ObserveActivityExploreCommentsOptions,
  ): Unsubscribe {
    return onSnapshot(
      buildInstagramReelCommentsQuery(reelId, options),
      (snapshot) => {
        callback(
          snapshot.docs.map((item) =>
            ActivityCommentConverter.fromFirestore(
              item.id,
              item.data() as Record<string, unknown>,
            ),
          ),
        )
      },
      (error) => {
        onError?.(ActivityCommentsErrorConverter.domainModel(error))
      },
    )
  }

  public observeWebinarSuggestionComments(
    suggestionId: string,
    callback: (comments: ActivityComment[]) => void,
    onError?: (error: Error) => void,
    options?: ObserveActivityExploreCommentsOptions,
  ): Unsubscribe {
    return onSnapshot(
      buildWebinarSuggestionCommentsQuery(suggestionId, options),
      (snapshot) => {
        callback(
          snapshot.docs.map((item) =>
            ActivityCommentConverter.fromFirestore(
              item.id,
              item.data() as Record<string, unknown>,
            ),
          ),
        )
      },
      (error) => {
        onError?.(ActivityCommentsErrorConverter.domainModel(error))
      },
    )
  }

  public observeSymptomComments(
    symptomId: string,
    whatWorksId: string,
    callback: (comments: ActivityComment[]) => void,
    onError?: (error: Error) => void,
    options?: ObserveActivityExploreCommentsOptions,
  ): Unsubscribe {
    return onSnapshot(
      buildSymptomCommentsQuery(symptomId, whatWorksId, options),
      (snapshot) => {
        callback(
          snapshot.docs.map((item) =>
            ActivityCommentConverter.fromFirestore(
              item.id,
              item.data() as Record<string, unknown>,
            ),
          ),
        )
      },
      (error) => {
        onError?.(ActivityCommentsErrorConverter.domainModel(error))
      },
    )
  }

  public async fetchNumberOfActivityExploreComments(
    activityExploreId: string,
  ): Promise<number> {
    try {
      const snapshot = await getCountFromServer(
        buildActivityExploreCommentsCountQuery(activityExploreId),
      )
      return snapshot.data().count
    } catch (error) {
      throw ActivityCommentsErrorConverter.domainModel(error)
    }
  }

  public async fetchNumberOfWebinarComments(
    webinarId: string,
  ): Promise<number> {
    try {
      const snapshot = await getCountFromServer(
        buildWebinarCommentsCountQuery(webinarId),
      )
      return snapshot.data().count
    } catch (error) {
      throw ActivityCommentsErrorConverter.domainModel(error)
    }
  }

  public async fetchNumberOfPodcastComments(
    podcastEpisodeId: string,
  ): Promise<number> {
    try {
      const snapshot = await getCountFromServer(
        buildPodcastCommentsCountQuery(podcastEpisodeId),
      )
      return snapshot.data().count
    } catch (error) {
      throw ActivityCommentsErrorConverter.domainModel(error)
    }
  }

  public async fetchNumberOfInstagramReelComments(
    reelId: string,
  ): Promise<number> {
    try {
      const snapshot = await getCountFromServer(
        buildInstagramReelCommentsCountQuery(reelId),
      )
      return snapshot.data().count
    } catch (error) {
      throw ActivityCommentsErrorConverter.domainModel(error)
    }
  }

  public async fetchNumberOfWebinarSuggestionComments(
    suggestionId: string,
  ): Promise<number> {
    try {
      const snapshot = await getCountFromServer(
        buildWebinarSuggestionCommentsCountQuery(suggestionId),
      )
      return snapshot.data().count
    } catch (error) {
      throw ActivityCommentsErrorConverter.domainModel(error)
    }
  }

  public async fetchNumberOfSymptomComments(
    symptomId: string,
    whatWorksId: string,
  ): Promise<number> {
    try {
      const snapshot = await getCountFromServer(
        buildSymptomCommentsCountQuery(symptomId, whatWorksId),
      )
      return snapshot.data().count
    } catch (error) {
      throw ActivityCommentsErrorConverter.domainModel(error)
    }
  }

  public observeNumberOfActivityExploreComments(
    activityExploreId: string,
    callback: (count: number) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    return onSnapshot(
      buildActivityExploreCommentsCountQuery(activityExploreId),
      (snapshot) => {
        callback(snapshot.size)
      },
      (error) => {
        onError?.(ActivityCommentsErrorConverter.domainModel(error))
      },
    )
  }

  public observeNumberOfWebinarComments(
    webinarId: string,
    callback: (count: number) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    return onSnapshot(
      buildWebinarCommentsCountQuery(webinarId),
      (snapshot) => {
        callback(snapshot.size)
      },
      (error) => {
        onError?.(ActivityCommentsErrorConverter.domainModel(error))
      },
    )
  }

  public observeNumberOfPodcastComments(
    podcastEpisodeId: string,
    callback: (count: number) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    return onSnapshot(
      buildPodcastCommentsCountQuery(podcastEpisodeId),
      (snapshot) => {
        callback(snapshot.size)
      },
      (error) => {
        onError?.(ActivityCommentsErrorConverter.domainModel(error))
      },
    )
  }

  public observeNumberOfInstagramReelComments(
    reelId: string,
    callback: (count: number) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    return onSnapshot(
      buildInstagramReelCommentsCountQuery(reelId),
      (snapshot) => {
        callback(snapshot.size)
      },
      (error) => {
        onError?.(ActivityCommentsErrorConverter.domainModel(error))
      },
    )
  }

  public observeNumberOfWebinarSuggestionComments(
    suggestionId: string,
    callback: (count: number) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    return onSnapshot(
      buildWebinarSuggestionCommentsCountQuery(suggestionId),
      (snapshot) => {
        callback(snapshot.size)
      },
      (error) => {
        onError?.(ActivityCommentsErrorConverter.domainModel(error))
      },
    )
  }

  public observeNumberOfSymptomComments(
    symptomId: string,
    whatWorksId: string,
    callback: (count: number) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    return onSnapshot(
      buildSymptomCommentsCountQuery(symptomId, whatWorksId),
      (snapshot) => {
        callback(snapshot.size)
      },
      (error) => {
        onError?.(ActivityCommentsErrorConverter.domainModel(error))
      },
    )
  }
}

const activityCommentsService = ActivityCommentsService.getInstance()

export async function createActivityComment(
  input: Partial<ActivityComment>,
): Promise<string> {
  return activityCommentsService.createActivityComment(input)
}

export async function fetchActivityComment(commentId: string): Promise<ActivityComment> {
  return activityCommentsService.fetchActivityComment(commentId)
}

export function observeActivityComment(
  commentId: string,
  callback: (comment: ActivityComment | null) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return activityCommentsService.observeActivityComment(commentId, callback, onError)
}

export async function updateActivityComment(
  commentId: string,
  comment: string,
): Promise<void> {
  return activityCommentsService.updateActivityComment(commentId, comment)
}

export async function deleteActivityComment(commentId: string): Promise<void> {
  return activityCommentsService.deleteActivityComment(commentId)
}

export async function likeComment(commentId: string, userId: string): Promise<void> {
  return activityCommentsService.likeComment(commentId, userId)
}

export async function unlikeComment(commentId: string, userId: string): Promise<void> {
  return activityCommentsService.unlikeComment(commentId, userId)
}

export async function createCommentReply(
  commentId: string,
  input: Partial<CommentReply>,
): Promise<string> {
  return activityCommentsService.createCommentReply(commentId, input)
}

export async function fetchActivityCommentReply(
  commentId: string,
  replyId: string,
): Promise<CommentReply> {
  return activityCommentsService.fetchActivityCommentReply(commentId, replyId)
}

export function observeCommentReplies(
  commentId: string,
  callback: (replies: CommentReply[]) => void,
  onError?: (error: Error) => void,
  options?: ObserveCommentRepliesOptions,
): Unsubscribe {
  return activityCommentsService.observeCommentReplies(
    commentId,
    callback,
    onError,
    options,
  )
}

export async function updateActivityCommentReply(
  commentId: string,
  replyId: string,
  commentReply: string,
): Promise<void> {
  return activityCommentsService.updateActivityCommentReply(
    commentId,
    replyId,
    commentReply,
  )
}

export async function deleteActivityCommentReply(
  commentId: string,
  replyId: string,
): Promise<void> {
  return activityCommentsService.deleteActivityCommentReply(commentId, replyId)
}

export async function likeCommentReply(
  commentId: string,
  replyId: string,
  userId: string,
): Promise<void> {
  return activityCommentsService.likeCommentReply(commentId, replyId, userId)
}

export async function unlikeCommentReply(
  commentId: string,
  replyId: string,
  userId: string,
): Promise<void> {
  return activityCommentsService.unlikeCommentReply(commentId, replyId, userId)
}

export async function fetchActivityExploreComments(
  activityExploreId: string,
  options?: ObserveActivityExploreCommentsOptions,
): Promise<ActivityComment[]> {
  return activityCommentsService.fetchActivityExploreComments(
    activityExploreId,
    options,
  )
}

export async function fetchWebinarComments(
  webinarId: string,
  options?: ObserveActivityExploreCommentsOptions,
): Promise<ActivityComment[]> {
  return activityCommentsService.fetchWebinarComments(webinarId, options)
}

export async function fetchPodcastComments(
  podcastEpisodeId: string,
  options?: ObserveActivityExploreCommentsOptions,
): Promise<ActivityComment[]> {
  return activityCommentsService.fetchPodcastComments(podcastEpisodeId, options)
}

export async function fetchInstagramReelComments(
  reelId: string,
  options?: ObserveActivityExploreCommentsOptions,
): Promise<ActivityComment[]> {
  return activityCommentsService.fetchInstagramReelComments(reelId, options)
}

export async function fetchWebinarSuggestionComments(
  suggestionId: string,
  options?: ObserveActivityExploreCommentsOptions,
): Promise<ActivityComment[]> {
  return activityCommentsService.fetchWebinarSuggestionComments(suggestionId, options)
}

export async function fetchSymptomComments(
  symptomId: string,
  whatWorksId: string,
  options?: ObserveActivityExploreCommentsOptions,
): Promise<ActivityComment[]> {
  return activityCommentsService.fetchSymptomComments(
    symptomId,
    whatWorksId,
    options,
  )
}

export function observeActivityExploreComments(
  activityExploreId: string,
  callback: (comments: ActivityComment[]) => void,
  onError?: (error: Error) => void,
  options?: ObserveActivityExploreCommentsOptions,
): Unsubscribe {
  return activityCommentsService.observeActivityExploreComments(
    activityExploreId,
    callback,
    onError,
    options,
  )
}

export function observeWebinarComments(
  webinarId: string,
  callback: (comments: ActivityComment[]) => void,
  onError?: (error: Error) => void,
  options?: ObserveActivityExploreCommentsOptions,
): Unsubscribe {
  return activityCommentsService.observeWebinarComments(
    webinarId,
    callback,
    onError,
    options,
  )
}

export function observePodcastComments(
  podcastEpisodeId: string,
  callback: (comments: ActivityComment[]) => void,
  onError?: (error: Error) => void,
  options?: ObserveActivityExploreCommentsOptions,
): Unsubscribe {
  return activityCommentsService.observePodcastComments(
    podcastEpisodeId,
    callback,
    onError,
    options,
  )
}

export function observeInstagramReelComments(
  reelId: string,
  callback: (comments: ActivityComment[]) => void,
  onError?: (error: Error) => void,
  options?: ObserveActivityExploreCommentsOptions,
): Unsubscribe {
  return activityCommentsService.observeInstagramReelComments(
    reelId,
    callback,
    onError,
    options,
  )
}

export function observeWebinarSuggestionComments(
  suggestionId: string,
  callback: (comments: ActivityComment[]) => void,
  onError?: (error: Error) => void,
  options?: ObserveActivityExploreCommentsOptions,
): Unsubscribe {
  return activityCommentsService.observeWebinarSuggestionComments(
    suggestionId,
    callback,
    onError,
    options,
  )
}

export function observeSymptomComments(
  symptomId: string,
  whatWorksId: string,
  callback: (comments: ActivityComment[]) => void,
  onError?: (error: Error) => void,
  options?: ObserveActivityExploreCommentsOptions,
): Unsubscribe {
  return activityCommentsService.observeSymptomComments(
    symptomId,
    whatWorksId,
    callback,
    onError,
    options,
  )
}

export async function fetchNumberOfActivityExploreComments(
  activityExploreId: string,
): Promise<number> {
  return activityCommentsService.fetchNumberOfActivityExploreComments(activityExploreId)
}

export async function fetchNumberOfWebinarComments(
  webinarId: string,
): Promise<number> {
  return activityCommentsService.fetchNumberOfWebinarComments(webinarId)
}

export async function fetchNumberOfPodcastComments(
  podcastEpisodeId: string,
): Promise<number> {
  return activityCommentsService.fetchNumberOfPodcastComments(podcastEpisodeId)
}

export async function fetchNumberOfInstagramReelComments(
  reelId: string,
): Promise<number> {
  return activityCommentsService.fetchNumberOfInstagramReelComments(reelId)
}

export async function fetchNumberOfWebinarSuggestionComments(
  suggestionId: string,
): Promise<number> {
  return activityCommentsService.fetchNumberOfWebinarSuggestionComments(suggestionId)
}

export async function fetchNumberOfSymptomComments(
  symptomId: string,
  whatWorksId: string,
): Promise<number> {
  return activityCommentsService.fetchNumberOfSymptomComments(
    symptomId,
    whatWorksId,
  )
}

export function observeNumberOfActivityExploreComments(
  activityExploreId: string,
  callback: (count: number) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return activityCommentsService.observeNumberOfActivityExploreComments(
    activityExploreId,
    callback,
    onError,
  )
}

export function observeNumberOfWebinarComments(
  webinarId: string,
  callback: (count: number) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return activityCommentsService.observeNumberOfWebinarComments(
    webinarId,
    callback,
    onError,
  )
}

export function observeNumberOfPodcastComments(
  podcastEpisodeId: string,
  callback: (count: number) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return activityCommentsService.observeNumberOfPodcastComments(
    podcastEpisodeId,
    callback,
    onError,
  )
}

export function observeNumberOfInstagramReelComments(
  reelId: string,
  callback: (count: number) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return activityCommentsService.observeNumberOfInstagramReelComments(
    reelId,
    callback,
    onError,
  )
}

export function observeNumberOfWebinarSuggestionComments(
  suggestionId: string,
  callback: (count: number) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return activityCommentsService.observeNumberOfWebinarSuggestionComments(
    suggestionId,
    callback,
    onError,
  )
}

export function observeNumberOfSymptomComments(
  symptomId: string,
  whatWorksId: string,
  callback: (count: number) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return activityCommentsService.observeNumberOfSymptomComments(
    symptomId,
    whatWorksId,
    callback,
    onError,
  )
}
